import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router";
import _ from "lodash";

import { BASE_URL } from "config/urls";
import { colors } from "theme/palette";
import { SUPPORT_EMAIL } from "utils/constants";
import { getToken, saveToken } from "utils/storage";

import FreshworksWidget from "components/FreshworksWidget";
import GlobalNotifications from "components/GlobalNotifications";
import Link from "components/Link";
import Stack from "components/Stack";
import Text from "components/Text";
import { FeatureDisabledContextProvider } from "components/useFeatureDisabledDialog";
import { FeatureLimitContextProvider } from "components/useFeatureLimitDialog";

import { getTokenBySession, useSignedUser } from "entities/Auth/sdk";

const PrivateRoutesGuard = () => {
  const location = useLocation();
  const [userAccountIsDisabled, setUserAccountIsDisabled] = useState(false);
  const [token, setToken] = useState(getToken());
  const { mutate: refetchUserData } = useSignedUser();

  useEffect(() => {
    if (!token) {
      // This is a part of our login flow
      // If there's a session, created by auth0, in the back-end - we will get our auth token here.
      getTokenBySession()
        .then(({ token }) => {
          // Save the returned token and re-render the current component
          saveToken(token);
          setToken(token);
        })
        .catch((error) => {
          if (error.message === "User account is disabled.") {
            setUserAccountIsDisabled(true);
            return;
          }

          const searchParams = _.isEmpty(location.search)
            ? `?next=${window.location.href}`
            : location.search;

          // If the current session on the backend didn't return a token - reroute to login
          window.location.href = `${BASE_URL}v2/users/login/${searchParams}`;
        });
    }
  }, [token, location]);

  useEffect(() => {
    if (token) {
      refetchUserData();
    }
  }, [token, refetchUserData]);

  if (userAccountIsDisabled) {
    return (
      <Stack
        sx={{
          top: 0,
          left: 0,
          padding: 5,
          zIndex: 1001,
          width: "100vw",
          height: "100vh",
          position: "absolute",
          backgroundColor: colors.white,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Text variant="h1" textAlign="center">
          Your account has not been activated yet. Please contact your company
          admin or email us at{" "}
          <Link
            to={`mailto:${SUPPORT_EMAIL}`}
            style={{ color: colors.blue100 }}
            data-testid="contact-support-link"
          >
            {SUPPORT_EMAIL}
          </Link>{" "}
          to do so.
        </Text>
      </Stack>
    );
  }

  if (!token) {
    return null;
  }

  return (
    <FeatureLimitContextProvider>
      <FeatureDisabledContextProvider>
        <GlobalNotifications />
        <FreshworksWidget />
        <Outlet />
      </FeatureDisabledContextProvider>
    </FeatureLimitContextProvider>
  );
};

export default PrivateRoutesGuard;
